// ============================================================================
// SYNC RETRY — shared retry logic for platform automation calls
// ============================================================================

const BUSY_PATTERNS = [
  "refreshing the session",
  "currently processing",
  "please try again",
  "please wait",
];

// Raw transport-layer noise from the Supabase Functions SDK / fetch.
// These messages don't tell an admin anything actionable, so we substitute
// a friendlier line at the boundary (once) before any logging or display.
const TRANSPORT_ERROR_PATTERNS = [
  "failed to send a request to the edge function",
  "failed to fetch",
  "networkerror",
  "functionsfetcherror",
  "timeout",
  "load failed",
];

const FRIENDLY_TRANSPORT_MESSAGE =
  "Couldn't reach the sync service. The platform may be temporarily unavailable — try resyncing in a moment.";

function friendlyTransportError(raw: string): string {
  const lower = raw.toLowerCase();
  return TRANSPORT_ERROR_PATTERNS.some((p) => lower.includes(p))
    ? FRIENDLY_TRANSPORT_MESSAGE
    : raw;
}

// Retry every 2 minutes, up to 20 attempts total
const RETRY_DELAY_MS = 2 * 60_000;
const MAX_SYNC_ATTEMPTS = 20;

export interface SyncResult {
  success: boolean;
  error?: string;
  attempts: number;
  data?: any;
}

/**
 * Retry a sync function with fixed 2-minute intervals (up to 5 attempts).
 * Detects "busy" patterns in error messages and retries automatically.
 */
export async function retrySyncWithBackoff(
  syncFn: () => Promise<{ error: any; data: any }>,
): Promise<SyncResult> {
  for (let attempt = 1; attempt <= MAX_SYNC_ATTEMPTS; attempt++) {
    const response = await syncFn();

    // Network-level error
    if (response.error) {
      const msg = response.error.message || String(response.error);
      const isBusy = BUSY_PATTERNS.some((p) => msg.toLowerCase().includes(p));
      if (!isBusy || attempt === MAX_SYNC_ATTEMPTS)
        return { success: false, error: friendlyTransportError(msg), attempts: attempt };
      await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
      continue;
    }

    // Check response body
    const data = response.data;
    if (data?.success === true) return { success: true, attempts: attempt, data };

    // success: false — check if busy
    const errors = (data?.platformResults || [])
      .filter((r: any) => !r.success)
      .map((r: any) => r.error || "");
    const allErrors = [...errors, data?.error || ""].join(" ").toLowerCase();
    const isBusy = BUSY_PATTERNS.some((p) => allErrors.includes(p));

    if (!isBusy || attempt === MAX_SYNC_ATTEMPTS) {
      return {
        success: false,
        error: errors.join("; ") || data?.error || "Sync failed",
        attempts: attempt,
        data,
      };
    }

    await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
  }
  return { success: false, error: "Platform busy after retries", attempts: MAX_SYNC_ATTEMPTS };
}
